import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://roofmate.app';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * Reusable SEO component. Drop one of these at the top of every public route.
 * - title: page title (will be suffixed with " | RoofMate" automatically unless raw=true)
 * - description: meta description (150-160 chars ideal)
 * - path: relative path for canonical url (e.g. "/", "/login")
 * - image: absolute URL to OG image (optional)
 * - keywords: comma-separated string (optional, low SEO weight but harmless)
 * - noIndex: set true to add robots noindex,nofollow (login/internal pages)
 * - jsonLd: object or array of objects for structured data
 * - raw: if true, title is used verbatim with no suffix
 */
export default function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  keywords,
  noIndex = false,
  jsonLd,
  raw = false,
}) {
  const fullTitle = raw || !title ? (title || 'RoofMate — Roof Measurement Software') : `${title} | RoofMate`;
  const canonical = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang="en-AU" />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {!noIndex && <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="RoofMate" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {ldArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
